/*
 * Copyright (c) 2025 Bolmate B.V.
 */
import './style/emailAdminInfo.scss';

import {AwesomeIcon, CloseButton, Link, Loader} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import classNames from 'classnames';

import React, {MouseEventHandler, useState} from 'react';

import {useTranslation} from 'react-i18next';

import EmailStep from '../../../../models/emailStepModel';

import {DUTCH_DATE_TIME_FORMAT} from '../../../../util/constants';

import {EmailActivity, EmailFlow, EmailStepAction} from '../../../../util/interfaces';

import {ProductSelectionHelper} from '../../../../util/productSelectionHelper';

interface Props {
    emailStep: EmailStep | null;
    open: boolean;
    loading: boolean;
    onClose: MouseEventHandler;
    onActivate: MouseEventHandler;
}

export default function EmailAdminInfo(props: Props) {
    const {t} = useTranslation();
    const {emailStep, open, loading, onClose, onActivate} = props;
    const [expandActivity, setExpandActivity] = useState(false);

    function onToggleExpand() {
        setExpandActivity(!expandActivity);
    }

    function renderField(label: string, value: string | number | undefined) {
        return (
            <div className='field'>
                <label className='label'>{label}</label>
                <label>{value ?? '-'}</label>
            </div>
        );
    }

    function renderProducts() {
        if (!emailStep) return null;
        const label = t('email.adminInfo.products', 'Producten');
        const helper = new ProductSelectionHelper(emailStep.productSelectionJSON.clauses);
        if (helper.isAllSelected()) return renderField(label, t('email.adminInfo.all', 'Alle'));
        else if (helper.isRemainingSelected()) return renderField(label, t('email.adminInfo.remaining', 'Resterende'));
        return renderField(label, 0);
    }

    function renderEmailType() {
        let value: string = '';
        if (emailStep?.flow === EmailFlow.INVOICE) value = t('email.adminInfo.invoiceMail', 'Factuurmail');
        else if (emailStep?.flow === EmailFlow.REVIEW_INVOICE)
            value = t('email.adminInfo.reviewInvoiceMail', 'Review + factuurmail');
        return renderField(t('email.adminInfo.mailType', 'Soort mail'), value);
    }

    function renderActivity() {
        if (!emailStep) return null;
        const {activity} = emailStep;
        const minRender = 5;
        const renderActivity = expandActivity ? activity : activity.slice(0, minRender);
        const toggleLabel = expandActivity
            ? t('email.adminInfo.showLess', 'Minder tonen')
            : t('email.adminInfo.showAll', 'Alles tonen');
        return (
            <div className='activity-container'>
                <label className='activity-title'>{t('email.adminInfo.activity', 'Activiteit')}</label>
                {renderActivity.map(renderActivityItem)}
                {activity.length > minRender && <Link onClick={onToggleExpand}>{toggleLabel}</Link>}
            </div>
        );
    }

    function renderActivityItem(item: EmailActivity, index: number) {
        return (
            <div className='activity-item' key={index}>
                <AwesomeIcon
                    name='power-off'
                    className={classNames('power-icon', item.action === EmailStepAction.ACTIVATE && 'active')}
                />
                <label>
                    {item.action === EmailStepAction.ACTIVATE
                        ? t('email.adminInfo.enabled', 'Ingeschakeld')
                        : t('email.adminInfo.disabled', 'Uitgeschakeld')}{' '}
                    {t('email.adminInfo.on', 'op')}
                </label>
                <label>{new IDate(item.date).format(DUTCH_DATE_TIME_FORMAT)}</label>
            </div>
        );
    }

    function renderInfoContainer() {
        if (!open) return null;
        return (
            <div className='email-info-container'>
                <CloseButton onClick={onClose} className='btn-close' />
                <Loader active={loading} />
                {renderEmailType()}
                {renderField(t('email.adminInfo.id', 'ID'), emailStep?.id)}
                {renderField(t('email.adminInfo.name', 'Naam'), emailStep?.label)}
                {renderField(
                    t('email.adminInfo.createdAt', 'Gemaakt op'),
                    emailStep?.createdAtMoment.format(DUTCH_DATE_TIME_FORMAT)
                )}
                {renderField(
                    t('email.adminInfo.updatedAt', 'Laatst gewijzigd'),
                    emailStep?.updatedAtMoment?.format(DUTCH_DATE_TIME_FORMAT)
                )}
                {renderProducts()}
                {renderField(
                    t('email.adminInfo.status', 'Status'),
                    emailStep?.active
                        ? t('email.adminInfo.active', 'Actief')
                        : t('email.adminInfo.inactive', 'Inactief')
                )}
                {renderField(
                    t('email.adminInfo.activeSince', 'Actief sinds'),
                    emailStep?.active ? (emailStep.activeSinceMoment?.format(DUTCH_DATE_TIME_FORMAT) ?? '-') : '-'
                )}
                {renderActivity()}
            </div>
        );
    }

    return (
        <div className={classNames('email-info', open && 'open')}>
            <AwesomeIcon name='info-circle' onClick={onActivate} className='info-icon' />
            {renderInfoContainer()}
        </div>
    );
}

EmailAdminInfo.displayName = 'EmailAdminInfo';
